import PropTypes from 'prop-types';
// @mui
import { Box, Typography, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
// components
import Iconify from '../../components/Iconify';
// hooks
import useLocales from '../../hooks/useLocales';

// ----------------------------------------------------------------------

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCustomer',
})(({ theme, isCustomer }) => ({
  maxWidth: '80%',
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(1),
  wordBreak: 'break-word',
  whiteSpace: 'pre-wrap',
  ...(isCustomer
    ? {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderBottomRightRadius: theme.direction === 'rtl' ? theme.spacing(2) : theme.spacing(0.5),
        borderBottomLeftRadius: theme.direction === 'rtl' ? theme.spacing(0.5) : theme.spacing(2),
      }
    : {
        backgroundColor: theme.palette.grey[200],
        color: theme.palette.text.primary,
        borderBottomLeftRadius: theme.direction === 'rtl' ? theme.spacing(2) : theme.spacing(0.5),
        borderBottomRightRadius: theme.direction === 'rtl' ? theme.spacing(0.5) : theme.spacing(2),
      }),
}));

// ----------------------------------------------------------------------

ChatMessage.propTypes = {
  message: PropTypes.object.isRequired,
};

export default function ChatMessage({ message }) {
  const { currentLang } = useLocales();
  const isCustomer = message.sender_type === 'customer' || message.role === 'user';
  const isSystem = message.sender_type === 'system';
  const isRtl = currentLang.direction === 'rtl';

  if (isSystem) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {message.content}
        </Typography>
      </Box>
    );
  }

  const getSenderLabel = () => {
    if (message.sender_type === 'operator') return isRtl ? 'فريق الدعم' : 'Support Team';
    if (message.sender_type === 'ai') return isRtl ? 'المساعد الآلي' : 'AI Assistant';
    return null;
  };
  const senderLabel = getSenderLabel();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isCustomer ? 'flex-end' : 'flex-start',
        mb: 0.5,
      }}
    >
      {senderLabel && (
        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.25, px: 1 }}>
          {senderLabel}
        </Typography>
      )}
      <MessageBubble isCustomer={isCustomer}>
        {message.content}
        {message.attachments && message.attachments.length > 0 && (
          <Box sx={{ mt: message.content ? 1 : 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {message.attachments.map((att) => {
              const isImage = att.content_type?.startsWith('image/');
              return isImage ? (
                <Box
                  key={att.id}
                  component="a"
                  href={att.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'block' }}
                >
                  <Box
                    component="img"
                    src={att.file}
                    alt={att.original_name}
                    sx={{ maxWidth: '100%', maxHeight: 160, borderRadius: 1, display: 'block' }}
                  />
                </Box>
              ) : (
                <Link
                  key={att.id}
                  href={att.file}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem', color: 'inherit' }}
                >
                  <Iconify icon="ic:baseline-attach-file" width={14} height={14} />
                  {att.original_name}
                </Link>
              );
            })}
          </Box>
        )}
      </MessageBubble>
      <Typography variant="caption" sx={{ color: 'text.disabled', px: 1, fontSize: '0.65rem' }}>
        {new Date(message.created_at).toLocaleTimeString(isRtl ? 'ar-SA' : 'en-US', {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </Typography>
    </Box>
  );
}
